import apiClient from './client';
import type {
  AlertThresholdDto,
  BudgetAllocateBatchDto,
  BudgetAllocateDto,
  ContractEpisodeCreditTransferDto,
  ContractProjectBudgetAllocateDto,
  ContractProjectBudgetPatchDto,
  ContractProjectBudgetRecallDto,
  EpisodeCreditTransferDto,
  BudgetOverview,
  BudgetRecallDto,
  BudgetTreeNode,
  CreditAllocation,
  OrgCreditBalance,
  OrgCreditLedgerList,
  OrgCreditLedgerParams,
  OrgProjectBudgetScope,
  OrgReportsSummary,
  OrgReportsSummaryParams,
  OrgTokenConsumptionList,
  OrgTokenConsumptionParams,
  ProjectBudgetLedgerList,
  ProjectBudgetLedgerParams,
  ProducerCreditLedgerList,
  ProducerCreditLedgerParams,
  RecallByProjectDto,
} from '@/types/org';

export const orgCreditsApi = {
  /** 查看组织词元余额 */
  async getBalance(orgId: string): Promise<OrgCreditBalance> {
    return apiClient.get<OrgCreditBalance>(
      `/orgs/${orgId}/credits/balance`
    ) as unknown as Promise<OrgCreditBalance>;
  },

  /** 为项目分拨 / 项目预算（RFC-006：不预扣组织实际余额，消耗时双重校验） */
  async allocateProjectBudget(orgId: string, dto: BudgetAllocateDto): Promise<CreditAllocation> {
    return apiClient.post<CreditAllocation>(`/orgs/${orgId}/credits/allocate`, {
      projectId: dto.projectId,
      amount: dto.amount,
      note: dto.note,
      producerUserId: dto.producerUserId,
    }) as unknown as Promise<CreditAllocation>;
  },

  /** 父级聚合根下多子 EPN 批量授予项目预算 */
  async allocateEpisodeBudgetsBatch(
    orgId: string,
    dto: BudgetAllocateBatchDto
  ): Promise<CreditAllocation[]> {
    return apiClient.post<CreditAllocation[]>(`/orgs/${orgId}/credits/allocate-episodes`, {
      parentProjectId: dto.parentProjectId,
      items: dto.items.map((i) => ({ projectId: i.projectId, amount: i.amount })),
      producerUserId: dto.producerUserId,
      note: dto.note,
    }) as unknown as Promise<CreditAllocation[]>;
  },

  /** 同父下两子项目间调拨项目预算，不改变组织实际余额 */
  async transferEpisodeCredits(orgId: string, dto: EpisodeCreditTransferDto): Promise<void> {
    await apiClient.post(`/orgs/${orgId}/credits/transfer-episodes`, {
      fromProjectId: dto.fromProjectId,
      toProjectId: dto.toProjectId,
      amount: dto.amount,
    });
  },

  /** 撤回项目预算（按分拨记录ID） */
  async recallProjectBudget(orgId: string, dto: BudgetRecallDto): Promise<CreditAllocation> {
    return apiClient.post<CreditAllocation>(`/orgs/${orgId}/credits/recall`, {
      allocationId: dto.allocationId,
      recallAmount: dto.recallAmount,
    }) as unknown as Promise<CreditAllocation>;
  },

  /** 按项目撤回剩余预算（前端友好，无需 allocationId） */
  async recallProjectBudgetByProject(
    orgId: string,
    dto: RecallByProjectDto
  ): Promise<CreditAllocation> {
    return apiClient.post<CreditAllocation>(`/orgs/${orgId}/credits/recall-by-project`, {
      projectId: dto.projectId,
      recallAmount: dto.recallAmount,
    }) as unknown as Promise<CreditAllocation>;
  },

  /** 列出所有项目预算概览（含成员数） */
  async listProjectBudgets(orgId: string): Promise<BudgetOverview[]> {
    return apiClient.get<BudgetOverview[]>(`/orgs/${orgId}/credits/projects`) as unknown as Promise<
      BudgetOverview[]
    >;
  },

  /** RFC-003 §9.1：按父子层级列出项目预算树（聚合节点 + 子项目）。 */
  async listProjectBudgetTree(
    orgId: string,
    params?: {
      memberUserId?: string | null;
      /** @deprecated 使用 projectScope */
      collabOnly?: boolean;
      /** team=团队项目，contract=承制项目（active incoming PCO） */
      projectScope?: OrgProjectBudgetScope;
      /** 一并返回项目内文件夹和单集 folderId */
      includeFolders?: boolean;
    }
  ): Promise<BudgetTreeNode[]> {
    const memberUserId =
      params?.memberUserId != null && params.memberUserId !== '' ? params.memberUserId : undefined;
    const projectScope =
      params?.projectScope ?? (params?.collabOnly === true ? ('contract' as const) : undefined);
    const includeFolders = params?.includeFolders === true ? true : undefined;
    const query =
      memberUserId !== undefined || projectScope !== undefined || includeFolders !== undefined
        ? {
            ...(memberUserId !== undefined ? { memberUserId } : {}),
            ...(projectScope !== undefined ? { projectScope } : {}),
            ...(includeFolders !== undefined ? { includeFolders } : {}),
          }
        : undefined;
    return apiClient.get<BudgetTreeNode[]>(`/orgs/${orgId}/credits/projects/tree`, {
      params: query,
    }) as unknown as Promise<BudgetTreeNode[]>;
  },

  /** 全包承制：分拨/增加承制项目父级总预算 */
  async allocateContractProjectBudget(
    orgId: string,
    dto: ContractProjectBudgetAllocateDto
  ): Promise<CreditAllocation> {
    return apiClient.post<CreditAllocation>(`/orgs/${orgId}/credits/contract-project-budget`, {
      pcoId: dto.pcoId,
      projectId: dto.projectId,
      amount: dto.amount,
      note: dto.note,
    }) as unknown as Promise<CreditAllocation>;
  },

  /** 全包承制：撤回承制项目剩余预算 */
  async recallContractProjectBudget(
    orgId: string,
    dto: ContractProjectBudgetRecallDto
  ): Promise<CreditAllocation> {
    return apiClient.post<CreditAllocation>(
      `/orgs/${orgId}/credits/contract-project-budget/recall`,
      {
        pcoId: dto.pcoId,
        projectId: dto.projectId,
        recallAmount: dto.recallAmount,
      }
    ) as unknown as Promise<CreditAllocation>;
  },

  /** 全包承制：更新单集预算或父项目单集默认预算 */
  async patchContractProjectEpisodeBudget(
    orgId: string,
    dto: ContractProjectBudgetPatchDto
  ): Promise<void> {
    await apiClient.patch(`/orgs/${orgId}/credits/contract-project-budget/episodes`, {
      pcoId: dto.pcoId,
      projectId: dto.projectId,
      aiCreditsBudget: dto.aiCreditsBudget,
    });
  },

  /** 全包承制：同父下两单集间调拨承制预算 */
  async transferContractEpisodeCredits(
    orgId: string,
    dto: ContractEpisodeCreditTransferDto
  ): Promise<void> {
    await apiClient.post(`/orgs/${orgId}/credits/contract-project-budget/transfer-episodes`, {
      pcoId: dto.pcoId,
      fromProjectId: dto.fromProjectId,
      toProjectId: dto.toProjectId,
      amount: dto.amount,
    });
  },

  /** 获取组织算力入账/修正明细 */
  async listCreditLedger(
    orgId: string,
    params: OrgCreditLedgerParams = {}
  ): Promise<OrgCreditLedgerList> {
    return apiClient.get<OrgCreditLedgerList>(`/orgs/${orgId}/credits/ledger`, {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        eventType: params.eventType,
      },
    }) as unknown as Promise<OrgCreditLedgerList>;
  },

  /** 获取组织 Token 消耗/退回日志 */
  async listTokenConsumptions(
    orgId: string,
    params: OrgTokenConsumptionParams = {}
  ): Promise<OrgTokenConsumptionList> {
    return apiClient.get<OrgTokenConsumptionList>(`/orgs/${orgId}/credits/token-consumption`, {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        projectId: params.projectId,
        operatorUserId: params.operatorUserId,
        trackType: params.trackType,
        featureType: params.featureType,
        range: params.range,
        startDate: params.startDate,
        endDate: params.endDate,
        tzOffset: params.tzOffset,
      },
    }) as unknown as Promise<OrgTokenConsumptionList>;
  },

  /** 获取制片人预算、项目分拨与项目消耗明细 */
  async listProducerCreditLedger(
    orgId: string,
    params: ProducerCreditLedgerParams = {}
  ): Promise<ProducerCreditLedgerList> {
    return apiClient.get<ProducerCreditLedgerList>(`/orgs/${orgId}/credits/producer-ledger`, {
      params: {
        view: params.view ?? 'all',
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        eventType: params.eventType,
        producerUserId: params.producerUserId,
        projectId: params.projectId,
        producerKeyword: params.producerKeyword,
        projectKeyword: params.projectKeyword,
      },
    }) as unknown as Promise<ProducerCreditLedgerList>;
  },

  /** 获取通用项目预算明细（团队项目预算 / 企业项目预算维度） */
  async listProjectBudgetLedger(
    orgId: string,
    params: ProjectBudgetLedgerParams = {}
  ): Promise<ProjectBudgetLedgerList> {
    return apiClient.get<ProjectBudgetLedgerList>(`/orgs/${orgId}/credits/project-budget-ledger`, {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        eventType: params.eventType,
        producerUserId: params.producerUserId,
        projectId: params.projectId,
        projectKeyword: params.projectKeyword,
      },
    }) as unknown as Promise<ProjectBudgetLedgerList>;
  },

  /** 设置词元预警阈值 */
  async setAlertThreshold(orgId: string, dto: AlertThresholdDto): Promise<void> {
    await apiClient.put(`/orgs/${orgId}/credits/alert-threshold`, {
      threshold: dto.threshold,
    });
  },

  /** 获取组织管理统计报表聚合数据（KPI + 趋势 + 排行 + 最新动态） */
  async getReportsSummary(
    orgId: string,
    params: OrgReportsSummaryParams = {}
  ): Promise<OrgReportsSummary> {
    return apiClient.get<OrgReportsSummary>(`/orgs/${orgId}/reports/summary`, {
      params: { range: params.range ?? '30d' },
    }) as unknown as Promise<OrgReportsSummary>;
  },
};
